import { useEffect, useState } from 'react'

export interface IWithdrawalDraft {
    fundId: number | null
    amount: string
    partialAccepted: boolean
    cardId: number | null
    note: string
}

const EMPTY: IWithdrawalDraft = {
    fundId: null,
    amount: '',
    partialAccepted: true,
    cardId: null,
    note: '',
}

const KEY = 'hamyan:withdrawal-draft'

/**
 * The wizard's answers, kept across an unmount.
 *
 * Step 3 can send the member off to `/manage-bank-cards` — plenty of members
 * have never registered an account. That unmounts the wizard, and without this
 * the "no fund picked" guard would bounce them back to step 1 having thrown
 * away everything they had answered. Exactly the bug reported against the loan
 * wizard: «when clicking back from the create-bank-account page, we return to
 * step 1 :(».
 *
 * **Restored in the `useState` initialiser, not an effect** — the step lives in
 * the URL, so on the first render back the wizard is already at `?step=4` and a
 * draft that only lands on the second render lets the guard fire first.
 *
 * sessionStorage rather than localStorage: a half-written request is
 * session-scoped by nature, and a stale one resurfacing tomorrow would be
 * mistaken for a real one.
 */
export default function useWithdrawalDraft() {
    const [draft, setDraft] = useState<IWithdrawalDraft>(() => {
        try {
            const raw = sessionStorage.getItem(KEY)
            return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY
        } catch {
            // a corrupt or unavailable store must not take the page down; the
            // worst case is an empty wizard, which is where it started
            return EMPTY
        }
    })

    useEffect(() => {
        try {
            sessionStorage.setItem(KEY, JSON.stringify(draft))
        } catch {
            // storage full or blocked — the wizard still works in-memory
        }
    }, [draft])

    const patch = (changes: Partial<IWithdrawalDraft>) =>
        setDraft((current) => ({ ...current, ...changes }))

    const clear = () => {
        setDraft(EMPTY)
        try {
            sessionStorage.removeItem(KEY)
        } catch {
            /* nothing to clean up */
        }
    }

    return { draft, patch, clear }
}
